/* store.js — the single zustand store for the Catalog Technician workspace.
 *
 * Two slices:
 *   blob — the persisted catalog (groups/handles/gola/carcass/appliances/
 *          colors/plinth/pmat/dstyles/countertops/lighting/fabrics/worktops/
 *          backsplash/equipment/pgCats). NEVER touched directly by views.
 *   ui   — ephemeral view state (never persisted).
 *
 * THE invariant (cat-arch.md §2): every data edit goes through mutate(fn):
 *   deep-clone blob → fn(next) → set → writeMirror(_v:2, ownerKeyFor) SYNC
 *   → debounced 800ms PUT /api/catalog/state {blob}.
 * UI-only changes go through setUi(patch) — no persistence side effects.
 *
 * Selector pattern (views): const carcass = useCatalog(s => s.blob.carcass); */
import { create } from "zustand";
import { stampUids } from "../configurator/engine/catalogIds";
import { writeMirror, createSaver } from "./lib/persistence";
import { projectLegacy, adoptLegacy } from "./lib/entities";
import { setPricingConfig } from "../configurator/engine/pricingSettings";
import { getTheme as getPlatformTheme } from "../lib/theme";

/* ONE theme engine (V5, 2026-08-05): the technician follows the PLATFORM
 * appearance choice (canonical lhc_theme; the old lhc-os-theme key is adopted
 * by the engine, so an existing dark-OS preference survives). */
function initTheme() {
  try { return getPlatformTheme(); }
  catch (e) { return "dark"; }
}

let toastSeq = 0;

export const useCatalog = create((set, get) => {
  const saver = createSaver((next, onlyIfCurrent) => {
    set((s) => {
      if (onlyIfCurrent && s.ui.saveState !== onlyIfCurrent) return {};
      return { ui: { ...s.ui, saveState: next } };
    });
  });

  return {
    blob: null,   // set by init() after boot fetch+normalize
    user: null,   // auth user — owner tag for the mirror

    ui: {
      osView: "dashboard",     // rail/view router (prototype osView; v4.56 default Dashboard)
      osLine: "kitchens",      // top-bar product family (kitchens|systems|indoor|outdoor)
      osCat: "kitchens",       // PIM family filter
      osSub: "all",            // collection chip
      osType: "all",           // type chip
      osSel: {},               // Products (threed) selection {sku:true}
      pimSel: {},              // Catalog (products) selection {idx:true}
      osThreeFocus: null,      // sku to highlight/scroll in threed
      osSku: null,             // open SKU (workbench view)
      osPGLibIdx: -1,          // open Fronts price-group card
      osPGCat: "colors",       // active PG_CATS tab
      osFabSel: { indoor: 0, leather: 0, outdoor: 0, rope: 0 }, // fabric zone selection
      theme: initTheme(),      // "dark" | "light" (persisted to lhc-os-theme)
      paletteOpen: false,      // command palette scrim
      toast: null,             // { msg, n } — see notify()
      saveState: "idle",       // idle | dirty | saving | saved | error
      carcImp: null,           // carcass-pricelist import wizard state
      prodImp: null,           // product import wizard state
      wizard: null,            // XLSX wizard modal state ("carcass"|"appliance"|{...})
    },

    /** Boot: normalized blob + auth user → state + first mirror write.
     *  ADOPTION LATCH (V2): normalize() adopts the legacy libraries into the
     *  component registry in memory. If the SAVED tenant had no registry yet,
     *  persist it once now — otherwise the registry would only reach the server
     *  the first time someone happened to edit something, and any surface
     *  reading entities server-side would see nothing. Idempotent: the latch
     *  (blob.libAdoptV1 on the saved doc) makes this a one-shot. */
    init(blob, user, savedHadRegistry) {
      /* DIMS HEAL LATCH (2026-08-24): normalize() re-derived product dims from
       * their uploaded model's measured size. That MUST reach the server —
       * the dealer's configurator reads the saved blob directly (normalize is
       * technician-side only), so an in-memory-only heal would leave the
       * customer seeing the wrong sizes. Marker is stripped before anything
       * is stored or mirrored. */
      const healed = !!(blob && blob.__dimsHealed);
      if (healed) delete blob.__dimsHealed;
      set({ blob, user });
      setPricingConfig(blob && blob.pricing);   // bench/editor pricing follows the tenant settings
      writeMirror(blob, user);
      if (healed
        || (!savedHadRegistry && blob && blob.entities && Object.keys(blob.entities).length)) {
        saver.schedule(blob);
      }
    },

    /** UI-only state patch — no mirror, no PUT. */
    setUi(patch) {
      set((s) => ({ ui: { ...s.ui, ...patch } }));
    },

    /** THE single data-mutation entrypoint. fn receives a deep clone of the
     *  blob and mutates it freely (prototype code style); the result is set,
     *  mirrored synchronously and PUT after the 800ms debounce. */
    mutate(fn) {
      const cur = get().blob;
      if (!cur) return;
      const next = JSON.parse(JSON.stringify(cur));
      fn(next);
      // COMPONENT REGISTRY sync (2026-08-09 data-loss fix; ORDER FIXED
      // 2026-08-15): the libraries are the MASTER for membership AND for
      // field edits — the Registry UI is gone, every edit happens on the
      // legacy arrays. ADOPT first (fold this edit into the entities), THEN
      // project (now value-identical, keeps slots/ids stable). The old
      // project-first order overwrote every registry-mapped field edit
      // (colour sw/tex, names, prices, finishes…) with the STALE entity value
      // on the same mutate — "I change the colour and it keeps the same
      // colors" — and then persisted the reverted blob.
      try { adoptLegacy(next); projectLegacy(next); } catch (e) { /* best-effort by contract */ }
      try { stampUids(next); } catch (e) { /* uids are additive — never block an edit */ }
      set({ blob: next });
      setPricingConfig(next && next.pricing);   // live edits apply immediately
      writeMirror(next, get().user);
      saver.schedule(next);
    },

    /** Toast (prototype notify()) — auto-clears after 2200ms. */
    notify(msg) {
      const n = ++toastSeq;
      set((s) => ({ ui: { ...s.ui, toast: { msg, n } } }));
      setTimeout(() => {
        const t = get().ui.toast;
        if (t && t.n === n) set((s) => ({ ui: { ...s.ui, toast: null } }));
      }, 2200);
    },
  };
});
